/**
 * Zentrale Utility-Funktionen für Bild-URLs
 * Konsolidiert die getImageUrl-Logik, die zuvor in 19+ Dateien dupliziert war
 */

import { getApiBaseUrl } from '../services/api/apiClient';

/**
 * Konvertiert einen relativen Pfad zu einer vollständigen Bild-URL
 * Erkennt automatisch die Umgebung (lokal vs. Production)
 *
 * @param path - Relativer Pfad (z.B. "/logos/stock/aapl.svg") oder vollständige URL
 * @returns Vollständige URL oder leerer String bei leerem Input
 *
 * @example
 * getImageUrl('/logos/stock/aapl.svg')
 * // Lokal: 'http://localhost:5555/logos/stock/aapl.svg'
 * // Production: 'https://renditefuchs.de/logos/stock/aapl.svg'
 *
 * getImageUrl('https://example.com/image.jpg')
 * // Gibt die URL unverändert zurück
 */
export const getImageUrl = (path: string): string => {
  if (!path) return '';
  // Hardcodierte localhost-URLs ersetzen (z.B. aus DB-Content)
  if (path.includes('://localhost:5555')) {
    return path.replace(/https?:\/\/localhost:5555/, getApiBaseUrl());
  }
  if (path.startsWith('http')) return path;
  // Lokale ObjectURLs (URL.createObjectURL) und Data-URIs sind bereits voll
  // adressierte Blob-/Inline-URLs — kein Prefix anhängen, sonst kaputt.
  if (path.startsWith('blob:') || path.startsWith('data:')) return path;
  return getApiBaseUrl() + path;
};

/**
 * Gibt die Backend-Base-URL basierend auf der Umgebung zurück
 * Nutzt die zentrale Logik aus apiClient.ts (unterstützt localhost, 127.0.0.1, lokale IPs)
 *
 * @returns Backend-URL
 */
export const getBackendBaseUrl = (): string => {
  return getApiBaseUrl();
};

/**
 * Prüft, ob die Anwendung lokal läuft
 *
 * @returns true wenn localhost oder 127.0.0.1 oder lokale IP, false in Production
 */
export const isLocalEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' ||
         hostname === '127.0.0.1' ||
         /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname);
};

/**
 * Generiert die URL für ein Media-Thumbnail
 *
 * @param fileName - Dateiname des Mediums (z.B. "1234567890-abc123.webp")
 * @returns Vollständige Thumbnail-URL
 *
 * @example
 * getMediaThumbnailUrl('1234567890-abc123.webp')
 * // Lokal: 'http://localhost:5555/blog/media/thumbnails/thumb-1234567890-abc123.webp'
 */
export const getMediaThumbnailUrl = (fileName: string): string => {
  const baseName = fileName.replace(/\.[^/.]+$/, '');
  return `${getBackendBaseUrl()}/blog/media/thumbnails/thumb-${baseName}.webp`;
};
